// Formatting + table helpers for the admin AI overview. Pure, unit-tested.

export const PRODUCT_LABELS = {
  bailey_assistant: 'Bailey assistant',
  receptionist_chat: 'Receptionist chat',
  receptionist_voice: 'Receptionist voice',
  sms: 'SMS',
  email: 'Email',
  numbers: 'Numbers',
  other_ai: 'Other AI'
};

export const KIND_LABELS = {
  bailey_turn: 'Bailey turn',
  bailey_title: 'Thread title',
  concierge_reply_web: 'Receptionist chat',
  concierge_reply_sms: 'Receptionist SMS',
  voice_call: 'Phone call',
  voice_turn: 'Call turn',
  sms_send: 'SMS',
  email_send: 'Email',
  number_rental: 'Number rental',
  import_ai: 'AI import',
  social_ai: 'Post studio',
  other_ai: 'Other AI'
};

export const FLAG_LABELS = {
  negative_margin: { label: 'Negative margin', tone: 'bad' },
  anomaly_pause: { label: 'Paused (anomaly)', tone: 'bad' },
  spike: { label: 'Spike vs 7-day', tone: 'warn' },
  rate_unverified: { label: 'Rate unverified', tone: 'warn' },
  out_of_credits: { label: 'Out of credits', tone: 'warn' },
  unattributed: { label: 'Unattributed', tone: 'dim' }
};

/** Pence → "£1,234.56". Small amounts keep precision: 0.42p → "£0.0042". */
export function fmtPence(p, opts = {}) {
  if (p == null || isNaN(p)) return '—';
  const pounds = Number(p) / 100;
  const abs = Math.abs(pounds);
  const decimals = opts.decimals != null ? opts.decimals : (abs > 0 && abs < 0.01 ? 4 : 2);
  const s = abs.toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return `${pounds < 0 ? '−' : ''}£${s}`;
}

export function fmtPct(n) {
  if (n == null || isNaN(n)) return '—';
  return `${Number(n).toFixed(1)}%`;
}

export function fmtInt(n) {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toLocaleString('en-GB');
}

/** 812 → "812 ms", 4300 → "4.3 s", 125000 → "2m 05s". */
export function fmtMs(ms) {
  if (ms == null || isNaN(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}

export function fmtTime(d, withDate = true) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  const time = dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return withDate ? `${dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${time}` : time;
}

export function fmtTokens(t) {
  if (!t) return '—';
  const parts = [`${fmtInt(t.input || 0)} in`, `${fmtInt(t.output || 0)} out`];
  if (t.cacheRead) parts.push(`${fmtInt(t.cacheRead)} cache read`);
  if (t.cacheWrite) parts.push(`${fmtInt(t.cacheWrite)} cache write`);
  return parts.join(' · ');
}

/** Reconciled = the provider reported a price (actualAt set). Nothing else. */
export function isReconciled(ledger) {
  return !!(ledger && ledger.actualAt);
}

/** The cost a line counts at: the reported actual once reconciled, else the estimate. */
export function lineCost(ledger) {
  if (!ledger) return null;
  return isReconciled(ledger) ? (Number(ledger.actualCostPence) || 0) : ledger.estCostPence;
}

/** Unit-economics rows for display (pence values, null-safe). */
export function unitRows(u) {
  if (!u) return [];
  const rc = u.receptionistConversation || {};
  const vm = u.voiceMinute || {};
  const bq = u.baileyQuestion || {};
  const rows = [
    { key: 'conv', label: 'Receptionist conversation', avg: rc.avgPerConversationPence, detail: `${fmtInt(rc.conversations)} conversations · ${fmtInt(rc.replies)} replies · ${fmtPence(rc.avgPerReplyPence)} per reply` },
    ...Object.entries(rc.byChannel || {}).map(([ch, c]) => ({ key: `conv-${ch}`, label: `  ${ch === 'sms' ? 'SMS' : 'Web chat'}`, avg: c.avgPerConversationPence, detail: `${fmtInt(c.conversations)} conversations`, sub: true })),
    { key: 'voice', label: 'Voice minute (all in)', avg: vm.avgPerMinutePence, detail: `${fmtInt(vm.calls)} calls · ${vm.minutes ?? 0} min talked · ${fmtInt(vm.billedMinutes)} billed · ${fmtPence(vm.avgPerCallPence)} per call` },
    { key: 'voice-tel', label: '  Telephony only', avg: vm.avgTelephonyPerMinutePence, detail: `model turns ${fmtPence(vm.modelCostPence)}`, sub: true },
    { key: 'bailey', label: 'Bailey question', avg: bq.avgPerQuestionPence, detail: `${fmtInt(bq.questions)} questions` },
    ...(bq.byModel || []).map((m) => ({
      key: `bq-${m.model}`, label: `  ${m.model}`, avg: m.avgPerQuestionPence, sub: true,
      detail: `${fmtInt(m.questions)} questions · avg ${fmtInt(m.avgTokens?.input)} in / ${fmtInt(m.avgTokens?.output)} out${m.avgTokens?.cacheRead ? ` / ${fmtInt(m.avgTokens.cacheRead)} cache read` : ''} · model ${fmtMs(m.avgModelLatencyMs)}`
    }))
  ];
  return rows;
}

/** "YYYY-MM" for the current month and the 11 before it (newest first). */
export function recentMonths(now = new Date(), n = 12) {
  const out = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

export function monthLabel(m) {
  const [y, mo] = String(m || '').split('-').map(Number);
  if (!y || !mo) return m || '';
  return new Date(y, mo - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

const SORTERS = {
  salonName: (r) => String(r.salonName || '').toLowerCase(),
  revenue: (r) => r.revenue || 0,
  cost: (r) => r.cost || 0,
  margin: (r) => r.margin || 0,
  marginPct: (r) => (r.marginPct == null ? -Infinity : r.marginPct),
  events: (r) => r.events || 0
};

/** Sort salon rows by a key, direction 'asc' | 'desc'. Never mutates. */
export function sortSalons(rows, key = 'cost', dir = 'desc') {
  const f = SORTERS[key] || SORTERS.cost;
  const sign = dir === 'asc' ? 1 : -1;
  return (rows || []).slice().sort((a, b) => {
    const x = f(a), y = f(b);
    if (x < y) return -1 * sign;
    if (x > y) return 1 * sign;
    return 0;
  });
}

/** Filter salon rows by name and/or a flag. */
export function filterSalons(rows, { search = '', flag = '' } = {}) {
  const q = search.trim().toLowerCase();
  return (rows || []).filter((r) =>
    (!q || String(r.salonName || '').toLowerCase().includes(q) || String(r.salonId || '').includes(q)) &&
    (!flag || (r.flags || []).includes(flag)));
}

/** Chart rows (pounds) from the salon daily series (pence). */
export function chartSeries(series) {
  return (series || []).map((d) => ({
    date: d.date.slice(5),
    Cost: Math.round((d.costPence || 0)) / 100,
    Revenue: Math.round((d.revenuePence || 0)) / 100
  }));
}

/** Where a session row drills to. */
export function sessionPath(drill) {
  if (!drill) return null;
  return `/beu-admin-x7k9/ai/session/${encodeURIComponent(drill.kind)}/${encodeURIComponent(drill.id)}`;
}
